
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ApolloQueryResult } from '@apollo/client';

import {
  NavBar,
  HelperView,
  RoundedIcon,
  BaseModal,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { CloseButton } from '@/modules/common/components/buttons';
import { PortabilityInfo } from './components/PortabilityInfo';
import { PortabilityValue } from './components/PortabilityValue';
import { PortabilityStatus } from './components/PortabilityStatus';
import {
  GetOrderDocument,
  GetOrderQuery,
  UpdateOrderQuoteValueDocument,
  TradeOrder
} from '@/modules/common/services/graphql/server/server.sdk';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { GtmTracker } from '@/modules/common/services/analytics';
import { ErrorPortability, ModalSteps } from './types';

@Component({
  components: {
    LoaderIndicator,
    BaseModal,
    NavBar,
    CloseButton,
    PortabilityInfo,
    PortabilityValue,
    PortabilityStatus,
    HelperView,
    RoundedIcon
  }
})
export default class PortabilityModal extends Vue {
  @Prop({ type: Boolean, default: false })
  private isOpen!: boolean;

  @Prop({ type: String, required: true })
  private id!: string;

  private step: number = ModalSteps.info;
  private ModalSteps = ModalSteps;
  private inputValue = 0;
  private portabilityData: TradeOrder | null = null;
  private loading = true;
  private error = false;
  private NebraskaColors = NebraskaColors;
  private successPortability = false;

  private async beforeMount() {
    await this.loadOrder();
  }

  @Watch('id')
  private async loadOrder() {
    this.loading = true;
    this.error = false;
    try {
      if (this.id) {
        const { data }: ApolloQueryResult<GetOrderQuery> =
          await this.$apollo.query({
            query: GetOrderDocument,
            variables: {
              orderId: this.id
            }
          });

        this.portabilityData = data.trade!.order as TradeOrder;
      }
    } catch {
      this.changeStep();
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  private updateInputValue(value: number) {
    this.inputValue = value;
  }

  private changeStep() {
    this.step = this.step + 1;
  }

  private closeModal() {
    this.$emit('close');
  }

  private get isDisabledInput() {
    return (
      this.inputValue === 0 ||
      this.inputValue === this.portabilityData?.executedPrice?.value
    );
  }

  private get getDisabledColor() {
    return this.isDisabledInput
      ? NebraskaColors.backgroundQuartenary
      : NebraskaColors.textPrimary;
  }

  private get computedTitle() {
    if (this.portabilityData) {
      const title = this.portabilityData.ticker;
      return this.step === ModalSteps.info
        ? `Portabilidade ${title}`
        : 'Alterar preço por ativo';
    }

    return 'Alterar preço por ativo';
  }

  private setChangedPrice() {
    this.loading = true;
    this.$apollo
      .mutate({
        mutation: UpdateOrderQuoteValueDocument,
        variables: {
          orderId: this.id,
          value: this.inputValue
        }
      })
      .then(() => {
        this.successPortability = true;

        GtmTracker.trackEvent({
          category: 'Trade',
          action: 'Alterou Preço por Ativo',
          label: this.portabilityData!.ticker
        });
      })
      .catch(error => {
        if (error.code === ErrorPortability.quoteNotChange) {
          this.successPortability = false;
        } else {
          this.error = true;
        }
      })
      .finally(() => {
        this.loading = false;
        this.changeStep();
      });
  }
}
